import React from 'react';

function getReleaseInfo() {
  const scripts = Array.from(document.scripts || [])
    .map(script => script.src || '')
    .filter(src => /\/static\/js\/main\.[^/]+\.js/.test(src));
  const bundle = scripts[scripts.length - 1] || '';
  return {
    app_version: '1.0.0',
    app_label: 'LottoTracker Launch',
    bundle: bundle.split('/').pop() || bundle || 'unknown',
    bundle_url: bundle || null,
    page_url: window.location.href,
    user_agent: window.navigator.userAgent,
    document_last_modified: document.lastModified || null,
    reported_at: new Date().toISOString(),
  };
}

function storeLocalError(payload) {
  try {
    const prior = JSON.parse(window.localStorage.getItem('lt:reactErrors') || '[]');
    window.localStorage.setItem('lt:reactErrors', JSON.stringify([payload, ...prior].slice(0, 10)));
  } catch {
    // Local logging should never create a second crash.
  }
}

function sendErrorReport(payload) {
  if (process.env.NODE_ENV !== 'production') return;
  try {
    window.fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        name: 'LottoTracker React error boundary',
        contact: 'system',
        trying_to_do: 'Render LottoTracker authenticated UI',
        feedback: [
          'React exception captured by global error boundary.',
          'Message: ' + payload.message,
          'Bundle: ' + payload.release.bundle,
          'Page: ' + payload.release.page_url,
          'Component stack:',
          payload.component_stack || 'Unavailable',
        ].join('\n'),
        page_url: payload.release.page_url,
        user_agent: payload.release.user_agent,
      }),
    }).catch(() => {});
  } catch {
    // Network logging must be best-effort only.
  }
}

function captureGlobalError(error, info) {
  const payload = {
    message: error?.message || 'Unknown React error',
    stack: error?.stack || '',
    component_stack: info?.componentStack || '',
    release: getReleaseInfo(),
  };
  // eslint-disable-next-line no-console
  console.error('LottoTracker React exception', payload);
  storeLocalError(payload);
  sendErrorReport(payload);
}

export class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    captureGlobalError(error, info);
  }

  reset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return <GlobalErrorRecovery onReset={this.reset} />;
  }
}

function GlobalErrorRecovery({ onReset }) {
  const refresh = () => {
    onReset();
    window.location.reload();
  };
  const home = () => {
    onReset();
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  return (
    <main style={{
      boxSizing: 'border-box',
      width: '100%',
      minWidth: 0,
      minHeight: '100svh',
      background: '#091d33',
      color: '#eef4fb',
      fontFamily: 'Arial, sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    }}>
      <section style={{
        boxSizing: 'border-box',
        minWidth: 0,
        width: 'min(560px, 100%)',
        background: '#102b46',
        border: '2px solid #b48a46',
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(0,0,0,.12)',
        padding: '34px 24px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>!</div>
        <h1 style={{
          margin: '0 0 8px',
          color: '#ffda7c',
          fontSize: 32,
          lineHeight: 1,
          textTransform: 'uppercase',
        }}>
          We hit a snag.
        </h1>
        <p style={{ color: '#c2d0de', lineHeight: 1.5, margin: '0 auto 20px', maxWidth: 440, overflowWrap: 'anywhere' }}>
          LottoTracker caught the issue before showing a blank screen. Refresh the app or return home to keep going.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={refresh} style={buttonStyle('#008577')}>Refresh</button>
          <button type="button" onClick={home} style={buttonStyle('#002855')}>Return Home</button>
        </div>
      </section>
    </main>
  );
}

function buttonStyle(background) {
  return {
    appearance: 'none',
    border: 'none',
    borderRadius: 8,
    background,
    color: '#fff',
    fontWeight: 700,
    padding: '11px 16px',
    cursor: 'pointer',
  };
}

export function installGlobalErrorLogging() {
  window.addEventListener('error', event => {
    const error = event.error || new Error(event.message || 'Unhandled window error');
    captureGlobalError(error, { componentStack: 'window.onerror' });
  });
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason || 'Unhandled promise rejection'));
    captureGlobalError(reason, { componentStack: 'window.onunhandledrejection' });
  });
}
